/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PROFILE_PAGE_IMPLEMENTATION
 * Date:         2025-12-13
 * Modified By:  Cursor
 *
 * Description:
 * Profile page for displaying seller/company profiles. Converted from modal
 * to dedicated page route for better navigation and shareability.
 *
 * Related Screens / Modules:
 * - client/src/components/ProfileModalContent.tsx (content display)
 * - client/src/services/usersService.ts (API integration)
 * - client/src/pages/ItemDetailPage/components/CompanyInformation.tsx (navigation trigger)
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Container, Spinner, Alert, Button } from "react-bootstrap";
import { User } from "../../interfaces/User";
import { getUserById } from "../../services/usersService";
import ProfileModalContent from "../../components/ProfileModalContent";
import ReviewCompanyForm from "../../components/setup/ReviewCompanyForm";
import "./ProfilePage.scss";

const ProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState<Partial<User> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get initial tab from query parameter, default to "company"
  const tabParam = searchParams.get("tab");
  const activeTab: "company" | "profile" = tabParam === "profile" ? "profile" : "company";

  // Fetch profile data on mount
  useEffect(() => {
    if (!userId) {
      setError("User ID is required");
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getUserById(userId);
        setProfile(response.data);
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || "Failed to load seller profile";
        setError(errorMessage);
        console.error("Error fetching seller profile:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  const handleBack = () => {
    navigate(-1); // Navigate to previous page
  };

  // Loading state
  if (loading) {
    return (
      <Container className="profile-page">
        <div className="profile-page__loading">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading seller profile...</p>
        </div>
      </Container>
    );
  }

  // Error state
  if (error || !profile) {
    return (
      <Container className="profile-page">
        <div className="profile-page__header">
          <Button variant="outline-secondary" onClick={handleBack}>
            ← Back
          </Button>
        </div>
        <Alert variant="danger" className="my-4">
          <Alert.Heading>Error Loading Profile</Alert.Heading>
          <p>{error || "Profile not found"}</p>
          <Button variant="outline-danger" onClick={handleBack}>
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  // Determine which view to show based on tab
  const isCompanyTab = activeTab === "company";

  return (
    <Container className="profile-page">
      {/* Header with back button */}
      <div className="profile-page__header">
        <Button variant="outline-secondary" onClick={handleBack}>
          ← Back
        </Button>
      </div>

      {/* Profile content */}
      <div className="profile-page__content">
        {isCompanyTab ? (
          // Show ReviewCompanyForm for company tab
          <ReviewCompanyForm profile={profile} showEditButton={false} wrapInContainer={false} />
        ) : (
          // Show ProfileModalContent for profile tab
          <ProfileModalContent
            profile={profile}
            initialTab="profile"
            showBroadcastSignature={false}
          />
        )}
      </div>
    </Container>
  );
};

export default ProfilePage;
